'use client';

import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import { RichText } from '@/components/richText/RichText';
import { SvgIcon } from '@/components/svgIcon';
import { getFormattedDMYDate } from '@/helper/dates';
import { linkToString } from '@/helper/urls';
import { getRatingText } from '@/helper/utils';
import { useETrustedReviews } from '@/providers/ETrustedReviewProvider';
import { useSettings } from '@/providers/SettingsProvider';
import BrandMark from '@/public/icons/eTrusted-brand-mark.svg';
import CombinationMark from '@/public/icons/eTrusted-combination-mark.svg';
import QuoteIcon from '@/public/icons/zitat_zeichen.svg';
import type { ComponentEtrustedTeaserStoryblok } from '@/types/component-types-sb';
import classNames from 'classnames';
import Link from 'next/link';
import { Trans, useTranslation } from 'react-i18next';

import classes from './ETrustedTeaser.module.scss';

function StarRating({ value }: { value?: number | null }) {
  const v = Math.min(5, Math.max(0, value ?? 0));
  const filled = Math.round(v);
  return (
    <span className={classes.starRating} role="img" aria-label={`${v} von 5 Sternen`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={classNames(classes.star, n <= filled ? classes.starOn : classes.starOff)} aria-hidden>
          ★
        </span>
      ))}
    </span>
  );
}

export type ETrustedTeaserProps = {
  blok: ComponentEtrustedTeaserStoryblok;
};

const ETrustedTeaser: React.FC<ETrustedTeaserProps> = ({ blok }) => {
  const { t } = useTranslation();
  const data = useETrustedReviews();
  const settings = useSettings();

  const rating = data?.rating;
  const ratingText = getRatingText(rating?.rating);

  const review = data?.review?.items[0]?.rating;
  const comment = data?.review?.items[0]?.comment;
  const reviewDate = getFormattedDMYDate(data?.review?.items[0]?.submittedAt);

  return (
    <div className={classNames(classes.eTrustedTeaser)}>
      <div className={classes.content}>
        <HeadlineField className={classes.headline} tag={HeadlineTag.H3}>
          {blok.headline}
        </HeadlineField>
        <RichText text={blok.text} />
        <Link href={linkToString(settings?.etrusted_link ?? '')} target={'_blank'} className={classes.eTrustedRating}>
          <div className={classes.ratingContainer}>
            <StarRating value={rating?.rating} />
            <span className={classes.ratingText}>{ratingText ?? ''}</span>
          </div>
          <SvgIcon className={classes.brandMark} component={BrandMark} inheritViewBox={true} />
        </Link>
      </div>

      <div className={classes.eTrustedWidget}>
        <SvgIcon className={classes.quoteIcon} component={QuoteIcon} inheritViewBox={true} />
        <StarRating value={review} />
        <p className={classes.comment}>{comment}</p>

        <hr className={classes.separator} />

        <div className={classes.widgetFooter}>
          <div className={classes.wrapper}>
            <div className={classes.verifiedRating}>{t('eTrustedTeaser.verifiedRating')}</div>
            <span className={classes.fromDate}>
              <Trans
                i18nKey={'eTrustedTeaser.fromDate'}
                values={{
                  date: reviewDate,
                }}
              />
            </span>
          </div>
          <SvgIcon className={classes.combinationMark} component={CombinationMark} inheritViewBox={true} />
        </div>
      </div>
    </div>
  );
};

export default ETrustedTeaser;
